import { ClientTaxStatus } from '../types/client';
import luhn from 'fast-luhn';

/**
 * Regex patterns for validation
 */
export const validationRegExpPatterns = {
  // Regex to validate email address
  emailPattern: /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/,
  // Regex to validate phone number
  mobilePhonePattern: /^(\+27|0)[6-8]\d{8}$/,
  // Regex to validate phone number
  phonePattern: /^(\d{7}|\d{10})$/,
  // Regex to validate address
  addressPattern: /^[\s\w/-]*$/,
  // Regex to validate postal code
  postalCodePatters: /^\d{4}$/,
  // Regex to validate name
  namePattern: /^[\s/A-Za-z-]+$/,
  // Regex to validate company name
  companyNamePattern: /^[\d\s/A-Za-z-]+$/,
  // Regex to validate date
  datePattern: /^([12]\d{3})\/(0[1-9]|1[0-2])\/([0-2]\d|3[01])$/,
};

/**
 * Error messages for field validations
 */
export const validationsErrorMessages = {
  date: 'Please check that you have entered a valid date (YYYY/MM/DD)',
  name: 'Only letters, spaces, hyphens are allowed',
  companyName: 'Only letters, spaces, hyphens and numbers are allowed',
  required: 'Please answer this',
  maxLength: 'Field value is too long',
  invalidCharacters: 'Field value includes invalid characters',
  phone: 'The format of this number needs to be like 0831234567 or 0111234567',
  mobilePhone: 'The format of this mobile number needs to be like 0831234567',
  email: 'Please check that you have entered a valid email address like john@doe.com',
  postalCode: 'Field value is not a valid postal code',
};

/**
 * Validate South African ID number
 * @param id ID to validate
 * @param gender Gender to validate
 * @param birthDate Date of birth to validate ("YYMMDD" format)
 * @returns True if the ID is valid, false otherwise
 */
export const validateAfricanId = (
  id: string,
  gender: 'male' | 'female',
  birthDate: string,
): boolean => {
  const normalizeBirthDate = birthDate.replace(/^(?:\d{2})?(\d{2})\/(\d{2})\/(\d{2})$/, '$1$2$3');
  if (id.length !== 13) {
    return false;
  }
  const matches = /^(\d{6})(\d{4})\d{3}$/.exec(id);
  if (!matches) {
    return false;
  }
  const [, idBirthDate, idGender] = matches;
  // Entered date of birth must match the ID date birth
  if (idBirthDate !== normalizeBirthDate) {
    return false;
  }
  // Entered gender must match the ID gender
  if (gender === 'male' && Number(idGender) < 5000) {
    return false;
  }

  return luhn(id);
};

/**
 * Validate passport ID
 */
export const validatePassportId = (id: string): boolean => {
  return /^[\d\s/a-z-]+$/i.test(id) && id.length <= 15;
};

/**
 * Validate Namibian ID number
 */
export const validateNamibianId = (id: string): boolean => {
  return /^\d{11}$/.test(id);
};

/**
 * Validate juristic Registration number
 * @param regNumber Registration number to validate
 * @param taxStatusId Tax status ID (validations are different for different tax statuses)
 * @returns True if the registration number is valid, false otherwise
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const validateJuristicRegNumber = (regNumber: string, taxStatusId: number): boolean => {
  if (
    [
      ClientTaxStatus.companyForTrust,
      ClientTaxStatus.naturalPersonForTrust,
      ClientTaxStatus.nonTaxableForTrust,
    ].includes(taxStatusId)
  ) {
    return /^i?(it|tm|tmp)[\d+/]{0,47}$/i.test(regNumber);
  }
  return /^\d{4}\/\d{6}\/\d{2}$/.test(regNumber);
};
